import type { ReactNode } from 'react'
import type { Diagnosis } from '../types'
import { SYMPTOM_EG } from '../mock/symptoms'
import { asset } from '../lib/asset'
import {
  GRANULE_LABEL, SCALE_LABEL, METRIC_INTRO, METRIC_LABEL,
  type Severity, type MetricDelta,
} from '../copy/explain'
import { IconGrid, IconTexture, IconTiles, IconCheck, IconAlert, IconX } from '../icons'

/**
 * 等级徽标。
 *
 * 背景：三项指标的取值域不同（格子是布尔，鳞片四档，叠瓦三档），
 *   但用户需要的是统一的「这项要紧吗」。
 * 设计意图：把各自的取值先映射成 none / warn / bad 三档严重度，再统一配色与图标。
 *   于是用户扫一眼三张卡就知道该看哪张。
 *
 *   三色都要留着（2026-09-11 用户裁决：「琥珀的可以有」）。中间一版按「只要检出
 *   就是红的」把琥珀撤了，当天又撤回来：格子纹检出确实可以无损切除，把它和
 *   「纹理已经长进画面、只能重画」画成同一个红，等于用颜色吓人。
 *   那一版要解决的问题——三张卡里最浅的恰好是唯一检出的那项、扫一眼找不到问题——
 *   现在由图上的红色叠加回答（见 ProblemMap），不由徽标回答。
 */
function Level({ severity, text }: { severity: Severity; text: string }) {
  const Ic = severity === 'none' ? IconCheck : severity === 'warn' ? IconAlert : IconX
  return <span className={`lv ${severity}`}><Ic size={13} />{text}</span>
}

function Row({ k, v }: { k: string; v: string | number }) {
  return <div className="kv-row"><dt>{k}</dt><dd>{v}</dd></div>
}

/**
 * 这一项处理前后变成了什么样，压在卡头下面。
 *
 * 背景：2026-09-12 用户在完成页上把上下两排卡画了个叉：「排序还不太对」
 *   「他们几个能不能上下合并一下」「感觉有点重复」。那时复诊读数自成一排卡
 *   （叠瓦指数 / 平坦区鳞片 / 格子纹），诊断又自成一排（格子纹 / 鳞片纹理 /
 *   叠瓦指数）——同一项指标在一页上出现两次、两种顺序、两套名字，
 *   读的人得自己把它们配对。
 * 设计意图：一项一张卡，前后读数与判定依据上下叠在一起。卡头的徽章改报**结果**
 *   （已清除 / 已压低 / 本来就干净 / 还没降下来），处理前的等级不丢：它就在
 *   前后对照那一行的 before 里（例如「8.4%（结构化）→ 1.6%（未检出）」）。
 * 关键约束：只有完成页给得出前后两个值。诊断闸、半路停下的那几屏没有 after，
 *   那时不摆这一格，卡头仍报处理前的等级——不能拿中间产物凑一个「后」。
 */
function Delta({ d }: { d: MetricDelta }) {
  return (
    <div className="metric-delta">
      {/* 前后相同时不画删除线：划掉一个没被替换的值，会让人以为原值失效了。 */}
      {d.before === d.after ? (
        <div className="delta-row">
          <span className="delta-after">{d.after}</span>
          <span className="delta-same">处理前后一致</span>
        </div>
      ) : (
        <div className="delta-row">
          <span className="delta-before">{d.before}</span>
          <span className="delta-arrow">→</span>
          <span className="delta-after">{d.after}</span>
        </div>
      )}
    </div>
  )
}

/**
 * 这一项没归零的原因，摆在卡末（`.metric-note` 那一格）。
 *
 * 它原先紧跟在前后读数下面。合进指标卡之后，格子纹那句有四行，把整张卡的说明与
 * 举例图往下推了 40 多像素，三张卡的举例图从此不齐——而「三张卡的示例块要齐」
 * 是 2026-09-11 用户裁决过的（「宽窄不一」）。所以它挪到卡末，与叠瓦那张卡的口径
 * 说明同一格：那一格本来就是「解释上面那些数」的位置。
 * 不折叠这件事不变——用户会自己拿图去复检，事后才发现比一开始就说明更糟。
 */
function Caveat({ d }: { d?: MetricDelta }) {
  if (!d?.caveat) return null
  return <p className="metric-note">{d.caveat}</p>
}

/**
 * 「这个现象长什么样」：卡上那句解释配一块真实的病灶截图。
 *
 * 背景：2026-09-11 用户裁决——「这些文案，最好也截一些 case，让用户更好感知这都是
 *   什么现象」。「格子纹」「鳞片」「叠瓦」都是用户此前没听过的词，一句话描述不出质地，
 *   看完仍然不知道该在自己那张图上找什么。
 * 设计意图：图取自一张真跑过管线、三项全部检出的四代复读图，位置由管线自己的判定挑
 *   （见 scripts/make_symptom_crops.py），不做美化，只裁剪与最近邻放大。
 * 关键约束：
 *   1. 图注开头必须点明这是举例（2026-09-11 用户裁决：「直接说：举例图 之类的就行了」）。
 *      页面上此刻正有一张用户自己的图，不点明就成了「你的图坏成这样」。
 *      原先写的是「示例，不是你这张图：另一张图上……」——一句话里三次强调同一件事，
 *      「举例图」三个字已经说完了。
 *   2. 图注里的倍数与尺寸来自生成脚本（symptoms.ts），不手写。
 *   3. 放大用 image-rendering: pixelated。浏览器默认的平滑会把两三像素的格纹糊掉，
 *      糊掉的正是要给人看的东西。
 */
function Eg({ k, children }: { k: keyof typeof SYMPTOM_EG; children: ReactNode }) {
  const e = SYMPTOM_EG[k]
  return (
    <figure className="metric-eg">
      {e.boostSrc ? (
        <div className="metric-eg-pair">
          <span><img src={asset(e.src)} alt="示例图上的一小块，原样" loading="lazy" /><i>原样</i></span>
          <span>
            <img src={asset(e.boostSrc)} alt="同一块画面，把明暗反差提高之后" loading="lazy" />
            <i>反差 ×{e.boost}</i>
          </span>
        </div>
      ) : (
        <img src={asset(e.src)} alt="示例图上的一小块" loading="lazy" />
      )}
      <figcaption>{children}</figcaption>
    </figure>
  )
}

/**
 * 诊断三卡：格子纹、鳞片、叠瓦指数。
 *
 * 背景：这三项是管线判定的全部依据，也是决定走哪条处理路径的输入。
 *   免登录用户能看到的就是这一屏，它要独立成立、看完能明白自己的图坏在哪。
 * 设计意图：每张卡都给「这项在说什么」的一句话，再给等级，最后给支撑等级的实测数值。
 *   数值用等宽字体并右对齐，因为用户会在多次运行之间横向比较它们。
 * 关键约束：
 *   1. 数值全部来自管线产物，不在前端做二次计算或换算，
 *      避免页面上的数和产物里的数对不上。
 *   2. 叠瓦一项的判级口径与另两项不同：管线判级前把画布归一到 canon_long_edge 再分块，
 *      所以百分比与分块数都是在归一画布上数的，阈值也是在那个口径上标定的。
 *      这一点必须在卡里写出来。不写的话，用户会拿「44 / 190 个分块」去对照自己那张
 *      1672×941 的原图，怎么数都数不出 190 块，然后判断这个页面在胡说。
 */
/**
 * 三项读数收成一条芯片，贴在图底下。
 *
 * 2026-09-11：「如果是老外，他们会怎么排版？」——Topaz / Magnific 这一类页
 * 不给每项一张卡，只给「名字 · 状态 · 一个数」。详细读数折在芯片下面，
 * 要核对口径的人自己展开。三项的严重度仍走徽章那套色（琥珀可以有）。
 */
export function DiagnosisStrip({ diag }: { diag: Diagnosis }) {
  const g = GRANULE_LABEL[diag.granule.level]
  const s = SCALE_LABEL[diag.scale_index.level]
  const top = diag.lattice.peaks_top[0]
  const si = diag.scale_index
  // 次序与称呼跟着 METRIC_ORDER / METRIC_LABEL 走：这条芯片与它下面那三张卡说的是
  // 同一批指标，两处排法不同的话，用户每换一处就要重新找一遍（2026-09-12）。
  const items = [
    {
      k: METRIC_LABEL.scale,
      severity: s.severity,
      badge: s.text,
      n: si.flagged_tiles ? `${si.scale_tile_pct}% 的画面` : '—',
    },
    {
      k: METRIC_LABEL.granule,
      severity: g.severity,
      badge: g.text,
      n: diag.granule.flagged_windows ? `${diag.granule.flagged_windows} 处` : '—',
    },
    {
      k: METRIC_LABEL.lattice,
      severity: (diag.lattice.detected ? 'warn' : 'none') as Severity,
      badge: diag.lattice.detected ? '已检出' : '未检出',
      // 芯片上只放一个「一眼能懂」的数（2026-09-11「这种都要说人话才行」）：
      // 原先是 `4.348 · 2.03 px`，前一个数没有单位也没有对照，读不出好坏。
      n: diag.lattice.detected ? `格眼 ${top?.period_px ?? '—'} 像素` : '—',
    },
  ]
  return (
    <div className="strip">
      {items.map(it => (
        <div className="strip-item" key={it.k}>
          <span className="strip-k">{it.k}</span>
          <div className="strip-v">
            <Level severity={it.severity} text={it.badge} />
            <span className="strip-n">{it.n}</span>
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * @param deltas 完成页才有：这一趟处理前后的逐项变化。给了就把它压在各自的卡头下面，
 *   卡头的徽章也跟着改报结果。不给（诊断闸、半路停下的那几屏）就只报处理前的判定。
 */
export function DiagnosisCards({ diag, deltas }: { diag: Diagnosis; deltas?: MetricDelta[] }) {
  const g = GRANULE_LABEL[diag.granule.level]
  const s = SCALE_LABEL[diag.scale_index.level]
  const top = diag.lattice.peaks_top[0]
  const si = diag.scale_index
  const d = (k: MetricDelta['key']) => deltas?.find(x => x.key === k)
  const dScale = d('scale')
  const dGranule = d('granule')
  const dLattice = d('lattice')
  const scaleNote = (
    <>
      {'这几个数是把你的图先缩到 '}{si.measured_on}{' 再数出来的（判定线也按这个尺寸定），'}
      {'所以别拿方块数去对照原图的像素——另外两项才是直接在原图上量的。'}
    </>
  )

  // 卡头的徽章：有前后变化时报结果，否则报处理前的等级。
  const badge = (delta: MetricDelta | undefined, severity: Severity, text: string) => (
    delta
      ? <span className={`lv ${delta.tone}`}>{delta.badge}</span>
      : <Level severity={severity} text={text} />
  )

  // 三张卡的次序取自 METRIC_ORDER（叠瓦 → 鳞片 → 格子纹），与复诊读数那句结论、
  // 与图下那条芯片同一个顺序。2026-09-12 之前这里是倒着排的，同一页上两种顺序。
  return (
    <div className="metrics">
      <section className="metric">
        <div className="metric-top">
          <div className="metric-ic"><IconTiles /></div>
          <div>
            <h4>{METRIC_LABEL.scale}</h4>
            {badge(dScale, s.severity, s.text)}
          </div>
        </div>
        {dScale && <Delta d={dScale} />}
        <p className="metric-intro">{METRIC_INTRO.scale}</p>
        <Eg k="scale">
          {'举例图 · '}
          {SYMPTOM_EG.scale.crop[0]}×{SYMPTOM_EG.scale.crop[1]}
          {' 像素。花瓣被画成一片排列整齐的鳞，这种地方没法靠滤镜清掉，'}
          {SYMPTOM_EG.scale.reading}{'。'}
        </Eg>
        <dl className="kv">
          <Row k="长鱼鳞的画面占" v={`${si.scale_tile_pct}%`} />
          <Row k="有问题的方块" v={`${si.flagged_tiles} / ${si.tiles} 块`} />
          <Row k="每块里鳞片占多少" v={`${si.median_cov_pct}%`} />
          <Row k="超过多少算只能重画" v={`${si.thresholds.structured_min_pct}%`} />
          <Row k="一块有多大" v={`${si.tile} 像素见方（每隔 ${si.stride} 像素取一块）`} />
        </dl>
        <Caveat d={dScale} />
        {si.measured_on && <p className="metric-note">{scaleNote}</p>}
      </section>

      <section className="metric">
        <div className="metric-top">
          <div className="metric-ic"><IconTexture /></div>
          <div>
            <h4>{METRIC_LABEL.granule}</h4>
            {badge(dGranule, g.severity, g.text)}
          </div>
        </div>
        {dGranule && <Delta d={dGranule} />}
        <p className="metric-intro">{METRIC_INTRO.granule}</p>
        <Eg k="granule">
          {'举例图 · 天空里 '}
          {SYMPTOM_EG.granule.crop[0]}×{SYMPTOM_EG.granule.crop[1]}
          {' 像素。本该是一片干净的渐变，实际铺着一层颗粒，'}
          {SYMPTOM_EG.granule.reading}{'。'}
        </Eg>
        {/* 「窗口」是我们取样时框出来的那一小块画面，用户不必知道这个词，
            只需要知道我们看了几处、其中几处不对劲。 */}
        <dl className="kv">
          <Row k="看着不对劲的地方" v={`${diag.granule.flagged_windows} 处`} />
          <Row k="查过的平坦区域" v={`${diag.granule.flat_windows.length} 处`} />
          <Row k="纹路顺着一个方向的" v={`${diag.granule.oriented_windows.length} 处`} />
          {diag.granule.flat_windows[0] && (
            <Row k="其中最花的那处，颗粒占了" v={`${diag.granule.flat_windows[0].mid_blob_cov_pct}%`} />
          )}
        </dl>
        <Caveat d={dGranule} />
      </section>

      <section className="metric">
        <div className="metric-top">
          <div className="metric-ic"><IconGrid /></div>
          <div>
            <h4>{METRIC_LABEL.lattice}</h4>
            {badge(
              dLattice,
              diag.lattice.detected ? 'warn' : 'none',
              diag.lattice.detected ? '已检出' : '未检出',
            )}
          </div>
        </div>
        {dLattice && <Delta d={dLattice} />}
        <p className="metric-intro">{METRIC_INTRO.lattice}</p>
        <Eg k="lattice">
          {'举例图 · '}
          {SYMPTOM_EG.lattice.crop[0]}×{SYMPTOM_EG.lattice.crop[1]}
          {' 像素的一块天空。左边是原样，看着是干净的；右边把明暗反差提到 '}
          {SYMPTOM_EG.lattice.boost}{' 倍，那层格子才显出来——它本身只有 '}
          {SYMPTOM_EG.lattice.levels}{' 个色阶深，所以印出来显脏，屏幕上却不容易看见。'}
        </Eg>
        {/* 标签一律说人话（2026-09-11 用户裁决）。数值原样来自管线产物，
            只是不再用「尖峰」「频点」这些只有写论文的人才用的称呼。 */}
        <dl className="kv">
          <Row k="网格有多明显" v={`高出周围 ${diag.lattice.max_peak_excess} 倍`} />
          <Row k="格眼多大" v={top?.period_px != null ? `${top.period_px} 像素一格` : '—'} />
          <Row k="要清掉多少处" v={`${diag.lattice.isolated_components_kept} 处`} />
          <Row k="占整张图的比重" v={`${diag.lattice.notched_bins_pct}%`} />
        </dl>
        <Caveat d={dLattice} />
      </section>
    </div>
  )
}
